import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {Link as RouterLink} from '@github-ui/react-router'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {BlockedIcon} from '@primer/octicons-react'
import {Link as PrimerLink, Token} from '@primer/react'

import {getParentIssueUrl} from '../../utils/urls'
import type {IssueSecondaryNode} from '../issue-viewer-secondary-query-types'
import styles from './HeaderBlockedBySummary.module.css'
import dividerLineStyles from './HeaderMetadataDivider.module.css'
import {useIssueHeaderDataContext} from './IssueHeaderDataContext'

export type HeaderBlockedBySummaryFragment = RelayCompat<
  Pick<IssueSecondaryNode, 'state' | 'issueDependenciesSummary' | 'topBlockedBy'>
>

export type HeaderBlockedBySummaryProps = {
  /** Overrides the state from the shared header data. */
  state?: HeaderBlockedBySummaryFragment['state']
  size?: 'xlarge' | 'small'
  repositoryNameWithOwner?: string
}

export const HeaderBlockedBySummary = ({
  state,
  size = 'xlarge',
  repositoryNameWithOwner,
}: HeaderBlockedBySummaryProps) => {
  const node = useIssueHeaderDataContext()
  const {blockedBy: blockedByCount} = node.issueDependenciesSummary ?? {}
  if (!blockedByCount) return <></>
  if ((state ?? node.state) === 'CLOSED') return <></>

  const blockedByIssue = blockedByCount === 1 ? node.topBlockedBy?.nodes?.[0] || undefined : undefined
  return (
    <>
      {size !== 'small' && <span className={dividerLineStyles.dividerLine} />}
      <HeaderBlockedBySummaryInternal
        blockedByCount={blockedByCount}
        size={size}
        blockedByIssue={blockedByIssue}
        repositoryNameWithOwner={repositoryNameWithOwner}
      />
    </>
  )
}

export type HeaderBlockedBySummaryInternalProps = {
  blockedByCount: number
  size: 'xlarge' | 'small'
  blockedByIssue?: {
    titleHTML: string
    number: number
    url: string
    repository: {
      name: string
      owner: {
        login: string
      }
    }
  }
  repositoryNameWithOwner?: string
}

function BlockedByIssueLink({
  blockedByIssue,
  size,
  repositoryNameWithOwner,
}: {
  blockedByIssue: NonNullable<HeaderBlockedBySummaryInternalProps['blockedByIssue']>
  size: 'xlarge' | 'small'
  repositoryNameWithOwner?: string
}) {
  const {titleHTML, number, url, repository} = blockedByIssue
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = getHovercardAttributesForType('issue', {
    owner: repository.owner.login,
    repo: repository.name,
    issueNumber: number,
  })
  const blockedByRepoNameWithOwner = `${repository.owner.login}/${repository.name}`
  const linkUrl = repositoryNameWithOwner
    ? getParentIssueUrl(repositoryNameWithOwner, blockedByRepoNameWithOwner, number, url)
    : url
  return (
    <PrimerLink
      as={RouterLink}
      className={styles.issueLink}
      aria-label={`Blocked by issue ${titleHTML}`}
      inline
      muted
      to={linkUrl}
      {...hovercardAttrs}
    >
      {size === 'small' ? (
        `#${number}`
      ) : (
        // Server-sanitized HTML from GraphQL titleHTML field
        // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string
        <VerifiedHTMLText html={titleHTML as SafeHTMLString} className="markdown-title" />
      )}
    </PrimerLink>
  )
}

function HeaderBlockedBySummaryInternal({
  blockedByCount,
  size,
  blockedByIssue,
  repositoryNameWithOwner,
}: HeaderBlockedBySummaryInternalProps) {
  const label = `Blocked by ${blockedByCount} issue${blockedByCount === 1 ? '' : 's'}`

  const blockedByIssueLink = blockedByIssue ? (
    <BlockedByIssueLink blockedByIssue={blockedByIssue} size={size} repositoryNameWithOwner={repositoryNameWithOwner} />
  ) : undefined

  if (size === 'small') {
    return (
      <div className={styles.smallSummary}>
        <BlockedIcon className="fgColor-danger" size={14} />
        <span className={styles.visualLabel}>{blockedByIssueLink || blockedByCount}</span>
        <span className="sr-only">{label}</span>
      </div>
    )
  }

  const leadingText = `Blocked by${blockedByIssueLink ? ':' : ''}`
  return (
    <Token
      text={
        <>
          <span className={styles.visualLabel}>
            {/* eslint-disable-next-line react-google-translate/no-conditional-text-nodes-with-siblings */}
            {leadingText} {blockedByIssueLink || blockedByCount}
          </span>
          <span className="sr-only">{label}</span>
        </>
      }
      leadingVisual={() => <BlockedIcon className="fgColor-danger" size={14} />}
      className={styles.token}
      size="xlarge"
    />
  )
}
